import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Modal,
  Fade,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";

const buildInitialItems = (record) =>
  (record?.line_items || []).map((item, index) => ({
    id: item.id || item["Item ID"] || `${item.item_code || "ITEM"}-${index}`,
    item_name: item.item_name || item["Item Name"] || "-",
    item_code: item.item_code || item["Item ID"] || item["Line No"] || "-",
    description: item.description || item.Description || "-",
    quantity: Number(item.quantity ?? item["Stock Qty"] ?? 0),
    unit: item.unit || item.uom || item.UOM || "Nos",
    unit_price: Number(item.unit_rate ?? item.rate ?? item["Unit Price"] ?? 0),
    tax_percent: Number(item.tax_percent ?? item["Tax %"] ?? item.tax ?? 0),
  }));

const PriceListQuotationView = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const selectedPriceList = state?.selectedPriceList || null;

  const [lineItems, setLineItems] = useState(buildInitialItems(selectedPriceList));
  const [deliveryDate, setDeliveryDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [validTillDate, setValidTillDate] = useState("");
  const [notes, setNotes] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const quotationDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [],
  );

  const handleQtyChange = (id, value) => {
    if (!/^\d*$/.test(value)) return;
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, quantity: Number(value || 0) } : item,
      ),
    );
  };

  const subtotal = lineItems.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.unit_price || 0),
    0,
  );
  const totalTax = lineItems.reduce((sum, item) => {
    const base = Number(item.quantity || 0) * Number(item.unit_price || 0);
    const tax = (base * Number(item.tax_percent || 0)) / 100;
    return sum + tax;
  }, 0);
  const grandTotal = subtotal + totalTax;

  const handleGenerateQuotation = () => {
    setShowSuccessModal(true);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <HeaderTitle titleText="Create Quotation" />
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
          }}
          onClick={() => navigate("/Buyer_dashboard/price_list")}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: "14px" }}>
          Price List No:{" "}
          <Box component="span" sx={{ fontFamily: "GilroyMedium" }}>
            {selectedPriceList?.document_number || "-"}
          </Box>
        </Typography>
        <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: "14px" }}>
          Supplier Name:{" "}
          <Box component="span" sx={{ fontFamily: "GilroyMedium" }}>
            {selectedPriceList?.vendor_name || "-"}
          </Box>
        </Typography>
        <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: "14px" }}>
          Date:{" "}
          <Box component="span" sx={{ fontFamily: "GilroyMedium" }}>
            {quotationDate}
          </Box>
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {[
                "S.No",
                "Item Name",
                "Item Code",
                "Description",
                "Quantity",
                "Unit",
                "Unit Price",
                "Tax %",
                "Tax Amount",
                "Total Amount",
              ].map((header, i) => (
                <TableCell
                  key={i}
                  sx={{
                    fontWeight: 600,
                    fontFamily: "GilroySemiBold",
                    backgroundColor: "#e0e9ee",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.map((item, index) => {
              const base = Number(item.quantity || 0) * Number(item.unit_price || 0);
              const taxAmount = (base * Number(item.tax_percent || 0)) / 100;
              const total = base + taxAmount;

              return (
                <TableRow key={item.id}>
                  <TableCell sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
                    {item.item_name}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
                    {item.item_code}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
                    {item.description}
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={item.quantity}
                      onChange={(e) => handleQtyChange(item.id, e.target.value)}
                      placeholder="0"
                      type="text"
                      sx={{
                        width: "84px",
                        "& .MuiInputBase-root": {
                          fontSize: "14px",
                          fontFamily: "GilroyMedium",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
                    {item.unit}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
                    {Number(item.unit_price).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
                    {item.tax_percent}%
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}>
                    {taxAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontFamily: "GilroySemiBold",
                      color: "#075b97",
                    }}
                  >
                    {total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      

      <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <TextField
          label="Expected Delivery Date"
          type="date"
          value={deliveryDate}
          onChange={(e) => setDeliveryDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            "& .MuiInputBase-root": {
              fontSize: "14px",
              fontFamily: "GilroyMedium",
            },
          }}
        />
       
        <TextField
          label="Valid Till Date"
          type="date"
          value={validTillDate}
          onChange={(e) => setValidTillDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            "& .MuiInputBase-root": {
              fontSize: "14px",
              fontFamily: "GilroyMedium",
            },
          }}
        />
       
      </Box>

      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: "#f0f7fa",
          border: "1px solid #cbd5e1",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxWidth: "400px",
          ml: "auto",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
            Subtotal
          </Typography>
          <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: "14px" }}>
            {subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
            Total Tax
          </Typography>
          <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: "14px" }}>
            {totalTax.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            sx={{ fontFamily: "GilroySemiBold", fontSize: "16px", color: "#075b97" }}
          >
            Grand Total
          </Typography>
          <Typography
            sx={{ fontFamily: "GilroySemiBold", fontSize: "18px", color: "#075b97" }}
          >
            {grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        <Button
          variant="outlined"
          sx={{
            textTransform: "none",
            fontFamily: "GilroyMedium",
            fontWeight: 600,
            fontSize: "15px",
            borderRadius: 1,
            px: 2.5,
            py: 1,
          }}
          onClick={() => navigate("/Buyer_dashboard/price_list")}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            textTransform: "none",
            fontFamily: "GilroyMedium",
            fontWeight: 600,
            fontSize: "15px",
            borderRadius: 1,
            px: 2.5,
            py: 1,
          }}
          onClick={handleGenerateQuotation}
        >
          Generate Quotation
        </Button>
      </Box>

      <Modal open={showSuccessModal}>
        <Fade in={showSuccessModal}>
          <Box>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText="Quotation Created Successfully!"
              popupDescription="Your quotation has been generated."
              popBtn={true}
              btnText="OK"
              vendorBtn={true}
              btnClick={() => {
                setShowSuccessModal(false);
                navigate("/Buyer_dashboard/price_list");
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default PriceListQuotationView;
